import { datadogRum } from '@datadog/browser-rum'
import isNil from 'lodash/isNil'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentsHttpClient } from '@omnea-app/documents/documentsHttpClient'
import { useToken } from '@omnea-app/fe-auth/TokenProvider'
import { BlobUrlHandle } from '@omnea-app/frontend/components/shared/file/BlobUrlHandle'
import { onFetchFailErrors } from '@omnea-app/frontend/utils/error-handling/fetchFailHandling'
import { isNotFoundApiError } from '@omnea-app/legacy/utils/error-handling/isApiErrorOfType'
import { ApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { uploadFilesToS3 } from './uploadFilesToS3'

const DocTypesViewableInBrowser = ['application/pdf', 'image/*']
const isDocTypeViewableInBrowser = (contentType: string) => {
  return (
    DocTypesViewableInBrowser.includes(contentType) ||
    DocTypesViewableInBrowser.some((type) => type.endsWith('/*') && contentType.startsWith(type.slice(0, -1)))
  )
}

export const useDocuments = () => {
  const { getToken } = useToken()
  const { t } = useTranslation('documents')
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)

  const uploadDocument = async (documents: File[]) => {
    setIsUploadingDocument(true)

    const documentsToUpload = documents.filter((document) => document.size > 0)
    if (documentsToUpload.length !== documents.length) {
      enqueueSnackbar(t('upload-document-warning', { documentCount: documents.length - documentsToUpload.length }), {
        variant: 'warning',
      })
    }

    try {
      const token = await getToken()
      const { uploaded, failedFileIndices, failedReasons } = await uploadFilesToS3(documentsToUpload, token)

      if (failedFileIndices.size > 0) {
        throw new Error('S3 upload failed for one or more files', { cause: failedReasons })
      }

      return uploaded
    } catch (error) {
      enqueueSnackbar(t('upload-document-error'), { variant: 'error' })
      datadogRum.addError(error, { messages: ['Document Upload to S3'] })
      return null
    } finally {
      setIsUploadingDocument(false)
    }
  }

  const getBlobUrlHandle = async (url: string): Promise<{ blobUrlHandle: BlobUrlHandle; isViewableInBrowser: boolean }> => {
    const response = await fetch(url)
    const contentType = response.headers.get('Content-Type') ?? ''
    const blob = await response.blob()
    const isViewableInBrowser = isDocTypeViewableInBrowser(contentType)

    return { blobUrlHandle: new BlobUrlHandle(blob), isViewableInBrowser }
  }

  const downloadDoc = async ({
    fileName,
    isViewableInBrowser,
    blobUrlHandle,
    forceDownload = false,
  }: {
    fileName: string
    isViewableInBrowser: boolean
    blobUrlHandle: BlobUrlHandle
    forceDownload?: boolean
  }) => {
    const link = document.createElement('a')
    if (isViewableInBrowser && !forceDownload) {
      if (!blobUrlHandle.url) return
      link.href = blobUrlHandle.url
      link.target = '_blank' // For preview
      link.rel = 'noopener noreferrer'
    } else {
      link.href = blobUrlHandle.url
    }

    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    blobUrlHandle.revoke()
  }

  const isForbiddenApiError = (error: unknown): error is { type: 'ForbiddenError'; detail: object } => {
    return !isNil(error) && typeof error === 'object' && 'type' in error && error.type === 'ForbiddenError'
  }

  const fetchSignedUrl = async (filestoreId: string) => {
    try {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      return await documentsClient.GET('/signed-url', {
        params: {
          query: {
            filestoreId,
          },
        },
      })
    } catch (error) {
      if (isNotFoundApiError(error)) {
        return {
          data: null,
          error: { type: 'NotFoundError' as const, detail: (error as any).detail },
        }
      }
      throw error
    }
  }

  const getSignedUrl = async ({ filestoreId, fileName }: { filestoreId: string; fileName: string }) => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId)

      if (error?.type === 'ForbiddenError') {
        throw new ApiError(error)
      }

      if (error?.type === 'NotFoundError') {
        return { notFound: true }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return null
      }

      return documentDownloadResponse.signedUrl
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      // Re-throw ForbiddenError so polling hook can handle it
      if (error instanceof Error) {
        throw error
      }
      // Then check if it's a raw API error that needs wrapping
      if (isForbiddenApiError(error)) {
        throw new ApiError(error)
      }
      return null
    }
  }

  const downloadDocument = async ({
    filestoreId,
    fileName,
    mode,
  }: {
    filestoreId: string
    fileName: string
    mode?: 'preview' | 'downloadFile'
  }): Promise<
    | { type: 'preview'; url: string }
    | { type: 'download' }
    | { type: 'pending'; message: string }
    | { type: 'quarantined'; message: string }
    | { type: 'notFound'; message: string }
    | null
  > => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId)

      if (error?.type === 'ForbiddenError') {
        return {
          type: 'quarantined',
          message: t('file.unavailable'),
        }
      }

      if (error?.type === 'NotFoundError') {
        return {
          type: 'notFound',
          message: t('file.not-found'),
        }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return {
          type: 'pending',
          message: documentDownloadResponse.message,
        }
      }

      const s3SignedUrl = documentDownloadResponse.signedUrl

      const { blobUrlHandle, isViewableInBrowser } = await getBlobUrlHandle(s3SignedUrl)
      if (mode !== 'preview' || !isViewableInBrowser) {
        await downloadDoc({
          fileName,
          isViewableInBrowser,
          blobUrlHandle,
          forceDownload: mode === 'downloadFile',
        })
        return { type: 'download' }
      } else {
        return { type: 'preview', url: s3SignedUrl }
      }
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      return null
    }
  }

  return { uploadDocument, isUploadingDocument, getSignedUrl, downloadDocument }
}
